<template>
  <div
    class="service-content-list"
    :class="{
      'service-content-list--empty-state': isEmptyState,
    }"
    :aria-busy="loading"
  >
    <div
      v-if="shouldShowSkeleton"
      class="service-content-list__skeleton"
      role="status"
      aria-label="内容加载中"
    >
      <a-skeleton :animation="true">
        <div class="service-content-list__skeleton-header">
          <a-skeleton-line :rows="1" :widths="['64px']" :line-height="18" />
          <a-skeleton-line :rows="1" :widths="['92px']" :line-height="18" />
        </div>
        <div
          v-for="(row, index) in contentSkeletonRows"
          :key="index"
          class="service-content-list__skeleton-item"
        >
          <a-skeleton-line :rows="1" :widths="[row.title]" :line-height="22" />
          <a-skeleton-line :rows="1" :widths="[row.meta]" :line-height="18" />
        </div>
      </a-skeleton>
    </div>

    <template v-else>
      <div class="service-content-list__header">
        <slot name="header" :total="total">
          <ContentListHeader :total="total" />
        </slot>
      </div>

      <div v-if="contentList.length > 0" class="service-content-list__items">
        <div
          v-for="item in contentList"
          :key="item.contentId ?? item.id"
          class="service-content-list__item"
          role="link"
          tabindex="0"
          @click="emit('item-click', item)"
          @keydown.enter="emit('item-click', item)"
        >
          <slot name="item" :item="item" :highlight-text="highlightText" />
        </div>
      </div>

      <div
        v-if="contentList.length > 0 && total > pageSize"
        class="service-content-list__pagination"
      >
        <SimplePagination
          :current="current"
          :total="totalPages"
          @change="emit('page-change', $event)"
        />
      </div>

      <div v-if="isEmptyState" class="service-content-list__empty">
        <img
          :src="withBase('/images/icons/empty/illustration_no_search.svg')"
          :alt="emptyAlt"
          class="service-content-list__empty-image"
        />
        <p class="service-content-list__empty-text">{{ emptyText }}</p>
      </div>
    </template>
  </div>
</template>

<script setup>
/**
 * @description 服务中心通用内容列表外壳，以帮助中心的布局和交互为基线。
 * 业务列表通过 header 与 item 插槽提供排序、摘要、元数据和操作。
 */
import { computed } from 'vue'
import SimplePagination from '@/components/base/SimplePagination.vue'
import ContentListHeader from '~/components/website/service/ContentListHeader.vue'
import { highlightServiceSearchText } from '~/utils/website/serviceContentSearch'
import { THEME_CSS_VARS } from '~/utils/theme'

const props = defineProps({
  contentList: { type: Array, default: () => [] },
  total: { type: Number, default: 0 },
  current: { type: Number, default: 1 },
  pageSize: { type: Number, default: 10 },
  loading: { type: Boolean, default: false },
  keyword: { type: String, default: '' },
  emptyAlt: { type: String, default: '暂无搜索结果' },
  emptyText: { type: String, default: '很遗憾，没有搜索到相关内容～' },
})

const emit = defineEmits(['page-change', 'item-click'])
const { withBase } = useBasePath()
const totalPages = computed(() => Math.max(1, Math.ceil(props.total / props.pageSize)))
const isEmptyState = computed(() => props.contentList.length === 0 && !props.loading)
const shouldShowSkeleton = computed(() => props.loading)
const contentSkeletonRows = [
  { title: '42%', meta: '24%' },
  { title: '56%', meta: '30%' },
  { title: '48%', meta: '22%' },
  { title: '62%', meta: '28%' },
]
const highlightText = (text) =>
  highlightServiceSearchText(text, props.keyword, THEME_CSS_VARS.primary)
</script>

<style lang="scss">
.service-content-list {
  width: 100%;
  height: 100%;
  padding: 24px 32px;
  border-radius: 12px;
  background-color: $bg-white;
  box-sizing: border-box;

  &--empty-state {
    border-radius: 12px;
  }

  &__header {
    margin-bottom: 24px;
    padding-bottom: 0;
  }

  &__skeleton {
    width: 100%;
  }

  &__skeleton-header {
    display: flex;
    height: 30px;
    margin-bottom: 24px;
    align-items: center;
    justify-content: space-between;
  }

  &__skeleton-item {
    display: flex;
    min-height: 80px;
    padding: 0 0 24px;
    flex-direction: column;
    gap: 12px;
    justify-content: center;
    border-bottom: 1px solid $border-dashed-color;
    box-sizing: border-box;

    & + & {
      padding-top: 24px;
    }
  }

  :deep(.service-content-list__skeleton .arco-skeleton-line) {
    margin: 0;
  }

  &__highlight {
    color: $primary-color;
    font-weight: inherit;
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-bottom: 24px;

    @include respond-to-max('lg') {
      gap: 12px;
      margin-bottom: 12px;
    }
  }

  &__item {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-bottom: 24px;
    box-sizing: border-box;
    cursor: pointer;
    transition: all 0.3s;

    &::after {
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      border-bottom: 1px solid $border-dashed-color;
      content: '';
    }

    &:hover,
    &:focus-visible {
      outline: none;

      .service-content-list__item-title {
        color: $primary-color;
      }
    }

    @include respond-to-max('lg') {
      min-height: 0;
      gap: 4px;
      padding-bottom: 12px;

      &:last-child {
        padding-bottom: 12px;
      }

      &:active {
        .service-content-list__item-title {
          color: $primary-color;
        }
      }
    }
  }

  &__item-title {
    display: -webkit-box;
    overflow: hidden;
    margin: 0;
    color: $text-title-color;
    font-size: $font-size-title-2-5;
    font-weight: 600;
    line-height: $line-height-title-2-5;
    line-clamp: 1;
    text-overflow: ellipsis;
    transition: color 0.3s;
    white-space: pre;
    word-break: break-all;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;

    @include respond-to-max('lg') {
      font-size: 14px;
      line-height: 22px;
    }
  }

  &__item-summary {
    display: -webkit-box;
    overflow: hidden;
    margin: 0;
    color: $text-content-color;
    font-size: $font-size-card-content;
    line-height: 22px;
    line-clamp: 2;
    word-break: break-all;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;

    @include respond-to-max('lg') {
      font-size: 13px;
      line-height: 20px;
      line-clamp: 3;
      -webkit-line-clamp: 3;
    }
  }

  &__item-products {
    margin: 0;
    color: $text-subtitle-color;
    font-size: $font-size-caption;
    line-height: $line-height-caption;

    @include respond-to-max('lg') {
      font-size: 12px;
      line-height: 18px;
    }
  }

  &__item-meta {
    margin: 0;
    color: $text-subtitle-color;
    font-size: $font-size-card-content;
    line-height: 22px;

    @include respond-to-max('lg') {
      font-size: 13px;
      line-height: 20px;
    }
  }

  &__item-products-content {
    display: -webkit-box;
    overflow: hidden;
    line-clamp: 2;
    word-break: break-all;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  &__item-products-label {
    margin-right: 4px;
  }

  &__item-products-names {
    display: inline;
  }

  &__item-update-time {
    display: none;
  }

  &__pagination {
    display: flex;
    justify-content: center;
    padding-top: 0;
    border-top: none;
  }

  &__empty {
    display: flex;
    min-height: 202px;
    padding: 24px 0;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  }

  &__empty-image {
    width: 202px;
    height: auto;
    margin-bottom: 12px;
    object-fit: contain;
  }

  &__empty-text {
    margin: 0;
    color: $text-subtitle-color;
    font-size: $font-size-card-content;
    line-height: 22px;
  }

  @include respond-to-max('lg') {
    padding: 12px 16px;
    border-radius: 12px;

    &__header {
      min-height: 30px;
      margin-bottom: 12px;
      padding: 4px 0;
      box-sizing: border-box;
    }

    &__skeleton-header {
      margin-bottom: 12px;
    }

    &__skeleton-item {
      min-height: 68px;
      padding-bottom: 12px;
      gap: 8px;

      & + & {
        padding-top: 12px;
      }
    }

    &__empty,
    &__empty-image {
      min-height: 202px;
    }

    &__empty-image {
      width: 202px;
      height: auto;
      min-height: 0;
      margin-bottom: 12px;
    }

    &__empty-text {
      font-size: 13px;
      line-height: 20px;
    }

    .content-list-header {
      font-size: 12px;
      line-height: 18px;
    }

    .content-list-header__count,
    .content-list-header__help-text,
    .content-list-header__help-link {
      font-size: 12px;
      line-height: 18px;
    }

    .content-list-header__help-icon {
      width: 12px;
      height: 12px;
    }
  }
}
</style>
